import { useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import type { AgentMemoryScope, AgentYamlConfig } from '@/lib/agentYaml'
import type { CreateAgentMemoryEntryRequest } from '@/types'
import { CreateMemoryDialog } from './CreateMemoryDialog'

const labelClass = 'text-sm font-bold leading-[160%] tracking-[0.015em] text-foreground'

const options: { value: AgentMemoryScope; title: string; chip: string; hint: string }[] = [
  {
    value: 'project',
    title: 'Project memory',
    chip: 'agent × project',
    hint: 'The agent remembers within a project but never carries context across projects — the only stateful option, and the safe multi-tenant choice.',
  },
  { value: 'none', title: 'No Memory', chip: 'none', hint: 'Every run starts from a clean slate.' },
]

interface AgentMemoryScopeFormProps {
  projectId: string
  memoryAgentId: string | null
  config: AgentYamlConfig
  onChange: (config: AgentYamlConfig) => void
  canAddMemory: boolean
  onQueueMemory: (entry: CreateAgentMemoryEntryRequest) => string | null
  disabled?: boolean
  addMemoryDisabled?: boolean
}

export function AgentMemoryScopeForm({
  projectId,
  memoryAgentId,
  config,
  onChange,
  canAddMemory,
  onQueueMemory,
  disabled = false,
  addMemoryDisabled = false,
}: AgentMemoryScopeFormProps) {
  const [createOpen, setCreateOpen] = useState(false)

  const addMemoryBlockedReason = config.memory === 'project' ? null : 'Choose Project memory to add memory.'

  return (
    <div className="space-y-4 rounded-2xl border border-border bg-card p-6" data-testid="agent-memory-scope-form">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <Label className={labelClass}>Memory scope</Label>
          <p className="text-sm text-muted-foreground">
            How durable memory is keyed. Memory is hydrated into the container before the run and persisted after — placed after the
            cache-stable system prompt so it never invalidates the prompt cache.
          </p>
        </div>
        {canAddMemory && (
          <Button
            type="button"
            className="shrink-0"
            onClick={() => setCreateOpen(true)}
            disabled={disabled || addMemoryDisabled || addMemoryBlockedReason !== null}
            title={addMemoryBlockedReason ?? undefined}
          >
            Add new Memory
          </Button>
        )}
      </div>
      <div className="space-y-2" role="radiogroup" aria-label="Memory scope">
        {options.map(option => (
          <label
            key={option.value}
            htmlFor={`agent-memory-${option.value}`}
            className={cn(
              'flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors',
              config.memory === option.value ? 'border-primary bg-primary/5' : 'border-border',
              disabled && 'cursor-not-allowed opacity-60',
            )}
          >
            <input
              id={`agent-memory-${option.value}`}
              type="radio"
              name="agent-memory-scope"
              value={option.value}
              checked={config.memory === option.value}
              onChange={() => onChange({ ...config, memory: option.value })}
              disabled={disabled}
              className="mt-1 accent-primary"
            />
            <span className="space-y-1">
              <span className="flex items-center gap-2">
                <span className="text-sm font-semibold text-foreground">{option.title}</span>
                <Badge variant="outline" className="px-2 py-0 text-[10px] font-medium text-muted-foreground">{option.chip}</Badge>
              </span>
              <span className="block text-xs text-muted-foreground">{option.hint}</span>
            </span>
          </label>
        ))}
      </div>
      <CreateMemoryDialog
        projectId={projectId}
        agentId={memoryAgentId}
        open={createOpen}
        onOpenChange={setCreateOpen}
        onQueue={memoryAgentId ? undefined : onQueueMemory}
      />
    </div>
  )
}
